import React, { useEffect, useState } from "react";
import { Col, Table, Spinner, Badge, Row, Card, Container } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import Swal from "sweetalert2";
import { FaArrowLeft } from "react-icons/fa";

const PRAMDView = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [purchaseData, setPurchaseData] = useState(null);
    const [packingcharges, setPackingcharges] = useState(0);
    const [packingchargesgst, setPackingchargesgst] = useState(0);
    const [rows, setRows] = useState([]);
    const [additionalCharges, setAdditionalCharges] = useState([]);

    const fetchPurchaseData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}purchaseReturn/findById/${id}`, {
                headers: authHeader()
            });

            const data = response.data;
            setPurchaseData(data);
            setPackingcharges(data.packingcharges || 0);
            setPackingchargesgst(data.packingchargesgst || 0);

            if (data.purchaseReturnItem && data.purchaseReturnItem.length > 0) {
                setRows(data.purchaseReturnItem.map(item => ({
                    description: item.product.description,
                    productremarks: item.productremarks || "",
                    quantity: item.quantity,
                    unitRateInr: item.unitrateinr,
                    amount: item.amount,
                    igst: item.gst,
                    totalInr: item.totalinr,
                    hsnsacno: item.hsnsacno,
                    partno: item.partno,
                    uom: item.uom,
                    serialnumber: item.serialnumber,
                    moc: item.moc || "",
                    type: item.type || "",
                    size: item.size || "",
                    rating: item.rating || "",
                    classes: item.classes || "",
                    schedule: item.schedule || "",
                    certification: item.certification || "",
                })));
            }

            if (data.additionalCharges && data.additionalCharges.length > 0) {
                setAdditionalCharges(data.additionalCharges.map(charge => ({
                    description: charge.chargetype,
                    chargepercent: charge.chargepercent,
                    amount: charge.chargeamount,
                    gstPercent: charge.chargegst,
                    totalAmount: charge.chargetotal
                })));
            }
        } catch (error) {
            console.error("Error fetching purchase return data", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to fetch purchase return data. Please try again.",
            });
            navigate("/purchases");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPurchaseData();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Draft':
                return 'secondary';
            case 'Submitted':
                return 'primary';
            case 'Approved':
                return 'success';
            case 'Rejected':
                return 'danger';
            case 'Completed':
                return 'info';
            case 'Open':
                return 'warning';
            default:
                return 'light';
        }
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(value || 0);

    // Calculate totals safely
    const productsTotal = rows.reduce((total, row) => total + (parseFloat(row.totalInr) || 0), 0);
    const additionalChargesTotal = additionalCharges.reduce((total, charge) =>
        total + (parseFloat(charge.totalAmount) || 0), 0);
    const packingChargesTotal = (parseFloat(packingcharges) || 0 +
        ((parseFloat(packingcharges) || 0) * ((parseFloat(packingchargesgst) || 0) / 100))) || 0;
    const grossTotal = productsTotal + additionalChargesTotal + packingChargesTotal;
    const grandTotal = Math.round(grossTotal); // Yahan round karo
    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!purchaseData) {
        return <div className="alert alert-danger text-center mt-4">Purchase return data not found</div>;
    }

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-primary mb-0">PURCHASE RETURN - VIEW</h5>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                            <FaArrowLeft className="me-1" />
                            Back
                        </button>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">Purchase No</small>
                                <div className="fw-bold">{purchaseData.purchaseinvoiceno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Purchase Date</small>
                                <div className="fw-bold">{formatDate(purchaseData.purchaseinvoicedate)}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Job Number</small>
                                <div className="fw-bold">{purchaseData.jobnumber || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge
                                        bg={getStatusBadge(purchaseData.status)}
                                        className="px-2 py-1"
                                    >
                                        {purchaseData.status || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Supplier Information */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Supplier Details</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Company Name</small>
                                <div className="fw-bold">{purchaseData.supplier?.companyname || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Contact Person</small>
                                <div className="fw-bold">{purchaseData.contactperson || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Phone</small>
                                <div className="fw-bold">{purchaseData.contactno || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Email</small>
                                <div className="fw-bold">{purchaseData.email || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div>
                                <small className="text-muted">Address</small>
                                <div className="fw-bold">
                                    {purchaseData.supplierMultipleAddress?.supplieraddress || '-'}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Invoice & Delivery Information */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Invoice & Delivery Information</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Supplier Invoice No</small>
                                <div className="fw-bold">{purchaseData.supplierinvoiceno || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Supplier Invoice Date</small>
                                <div className="fw-bold">{formatDate(purchaseData.supplierinvoicedate)}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Branch</small>
                                <div className="fw-bold">{purchaseData.branch?.branchname || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Warehouse</small>
                                <div className="fw-bold">{purchaseData.warehouse?.warehousename || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Other Reference</small>
                                <div className="fw-bold">{purchaseData.otherreference || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Delivery Address</small>
                                <div className="fw-bold">{purchaseData.shipto || '-'}</div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Purchase Return Items */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Purchase Return Items</h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center fw-bold py-2">#</th>
                                    <th className="fw-bold py-2">Product Description</th>
                                    <th className="fw-bold py-2">Part No</th>
                                    <th className="fw-bold py-2">HSN Code</th>
                                    <th className="fw-bold py-2 text-center">Qty</th>
                                    <th className="fw-bold py-2 text-center">UOM</th>
                                    <th className="fw-bold py-2 text-end">Unit Price</th>
                                    <th className="fw-bold py-2 text-end">Amount</th>
                                    <th className="fw-bold py-2 text-center">GST %</th>
                                    <th className="fw-bold py-2 text-end">Total Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            <div className="fw-semibold">{row.description}</div>
                                            {row.productremarks && (
                                                <div className="text-muted small mt-1">
                                                    <small><strong>Remarks:</strong> {row.productremarks}</small>
                                                </div>
                                            )}
                                            <div className="text-muted small mt-1">
                                                {[
                                                    { label: "MOC", value: row.moc },
                                                    { label: "Type", value: row.type },
                                                    { label: "Size", value: row.size },
                                                    { label: "Rating", value: row.rating },
                                                    { label: "Class", value: row.classes },
                                                    { label: "Schedule", value: row.schedule },
                                                    { label: "Certification", value: row.certification },
                                                ].filter(item => item.value).map((item, idx) => (
                                                    <div key={idx}>
                                                        <small><strong>{item.label}:</strong> {item.value}</small>
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                        <td>{row.partno || '-'}</td>
                                        <td>{row.hsnsacno || '-'}</td>
                                        <td className="text-center">{row.quantity || '0'}</td>
                                        <td className="text-center">{row.uom || '-'}</td>
                                        <td className="text-end">{formatCurrency(row.unitRateInr)}</td>
                                        <td className="text-end">{formatCurrency(row.amount)}</td>
                                        <td className="text-center">{row.igst || '0'}%</td>
                                        <td className="text-end fw-bold">{formatCurrency(row.totalInr)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <td colSpan="9" className="text-end fw-bold py-2">
                                        Products Total:
                                    </td>
                                    <td className="text-end fw-bold py-2">
                                        {formatCurrency(productsTotal)}
                                    </td>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Additional Charges */}
            {additionalCharges.length > 0 && (
                <Card className="shadow-sm border-0 rounded mb-3">
                    <Card.Header className="bg-light py-2">
                        <h6 className="mb-0 fw-bold">Additional Charges</h6>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="table-responsive">
                            <Table bordered size="sm" className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="fw-bold py-2">#</th>
                                        <th className="fw-bold py-2">Charge Type</th>
                                        <th className="fw-bold py-2 text-end">Charge %</th>
                                        <th className="fw-bold py-2 text-end">Amount</th>
                                        <th className="fw-bold py-2 text-center">GST %</th>
                                        <th className="fw-bold py-2 text-end">Total Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {additionalCharges.map((charge, index) => (
                                        <tr key={index}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>{charge.description || '-'}</td>
                                            <td className="text-end">{charge.chargepercent || '0'}%</td>
                                            <td className="text-end">{formatCurrency(charge.amount)}</td>
                                            <td className="text-center">{charge.gstPercent || '0'}%</td>
                                            <td className="text-end fw-bold">{formatCurrency(charge.totalAmount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-light">
                                    <tr>
                                        <td colSpan="5" className="text-end fw-bold py-2">
                                            Charges Total:
                                        </td>
                                        <td className="text-end fw-bold py-2">
                                            {formatCurrency(additionalChargesTotal)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Summary Section */}
            <Row>
                <Col lg={6} className="ms-auto">
                    <Card className="shadow-sm border-0 rounded">
                        <Card.Header className="bg-light py-2">
                            <h6 className="mb-0 fw-bold text-center">Purchase Return Summary</h6>
                        </Card.Header>
                        <Card.Body className="p-3">
                            <div className="space-y-2">
                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Products Total:</span>
                                    <span className="fw-bold">{formatCurrency(productsTotal)}</span>
                                </div>

                                {additionalCharges.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                        <span className="fw-semibold">Additional Charges:</span>
                                        <span className="fw-bold">{formatCurrency(additionalChargesTotal)}</span>
                                    </div>
                                )}

                                {/* Gross Total Show karo agar rounded se different hai */}
                                {grandTotal !== grossTotal && (
                                    <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                        <span className="fw-semibold">Gross Total:</span>
                                        <span className="fw-bold">{formatCurrency(grossTotal)}</span>
                                    </div>
                                )}

                                <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                    <span className="fw-bold">Grand Total (Rounded)</span>
                                    <span className="fw-bold text-primary">{formatCurrency(grandTotal)}</span>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate(-1)}
                >
                    <FaArrowLeft className="me-1" />
                    Back to List
                </button>
            </div>
        </Container>
    );
};

export default PRAMDView;